// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ProgressionPatternCharts from "src/components/containers/shared/ProgressionPatternCharts/ProgressionPatternCharts";
import ProgressionExternalExamRow from "src/components/containers/shared/ProgressionExternalExamRow/ProgressionExternalExamRow";
import Alert from "src/components/shared/alerts/Alert/Alert";

// Constants
import { MAX_EXAM_SELECTION } from "src/constants/enums";

// Types
import {
  EnrichedExternalExamType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

type PatternChartContainerType = {
  selectedRetinaLogikExams: Array<SelectedProgressionExamType>;
  selectedExternalExams?: Array<SelectedProgressionExamType>;
};

// The picker row carries a subset of the report shape: same measurements, different
// field names for source and date, and no images.
const toEnrichedExternalExam = (
  exam: SelectedProgressionExamType
): EnrichedExternalExamType =>
  ({
    ...exam,
    sourcePlatform: exam.source,
    date: exam.completionDate,
    isRetinaLogikExam: false,
  }) as unknown as EnrichedExternalExamType;

const PatternChartContainer = ({
  selectedRetinaLogikExams,
  selectedExternalExams = [],
}: PatternChartContainerType) => {
  const { t } = useTranslation();

  const hasSelection =
    selectedRetinaLogikExams.length > 0 || selectedExternalExams.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: { xs: "100%", md: "70%" },
        padding: "20px",
        gap: "20px",
        flex: 1,
        overflowY: { xs: "visible", md: "auto" },
      }}
    >
      {!hasSelection ? (
        <Alert
          variant="info"
          props={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
          alertContent={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontSize="14px" fontWeight={600}>
                {t("You can select a maximum of {{ maxNum }} exams.", {
                  maxNum: MAX_EXAM_SELECTION,
                })}
              </Typography>
              <Typography fontSize="14px">
                {t(
                  "Imported exams appear here only once their measurements have been processed."
                )}
              </Typography>
            </Box>
          }
        />
      ) : (
        <>
          {selectedRetinaLogikExams.map(
            (examSection) =>
              examSection.reportData && (
                <ProgressionPatternCharts
                  key={examSection.id}
                  examReport={examSection.reportData}
                  examData={examSection}
                />
              )
          )}
          {selectedExternalExams.map((externalExam) => (
            <ProgressionExternalExamRow
              key={externalExam.id}
              externalExam={toEnrichedExternalExam(externalExam)}
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default PatternChartContainer;
